import { Button, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { CustomerAccount, CustomerAdvancePayment, Page } from "@zhihaoscm/entity";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { BaseItem as ZhBaseItem, DetailHeader as ZhDetailHeader, Grid as ZhGrid } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { formatNumber } from "@/utils/number";
import type { ServiceCostBillRow } from "../ServiceCostBillPage";
import type { BillRecordProps, AccountBillQuery } from "./components/billRecord";
import type { WillPayQuery } from "../willPay/WillPayPage";
import type { WillPayRecordProps } from "./components/willPayRecord";
import BillRecord from "./components/billRecord";
import WillPayRecord from "./components/willPayRecord";

export interface UserAccountDetailPageProps {
    accountId?: number | string;
    loadAccount?: (id: number | string) => Promise<CustomerAccount>;
    loadAdvancePayments?: NonNullable<WillPayRecordProps["loadRecords"]>;
    loadBills?: NonNullable<BillRecordProps["loadBills"]>;
    revokeAdvancePayment?: NonNullable<WillPayRecordProps["revokeRecord"]>;
    verifyBill?: (params: {
        id: number | string;
        verificationAmount: number;
        verificationTime: string;
        verificationFileId?: number;
    }) => Promise<unknown>;
    revokeBill?: NonNullable<BillRecordProps["revokeBill"]>;
}

/** 复用 Vue 用户账户详情接口。 */
export async function loadUserAccount(id: number | string): Promise<CustomerAccount> {
    const response = await request.service.get<CustomerAccount>(`/admin/customer/account/${id}`);
    return response.data;
}

/** 复用 Vue 预付款记录分页接口和 customerId payload。 */
export async function loadUserAdvancePayments(
    query: WillPayQuery & { customerId: number }
): Promise<Page<CustomerAdvancePayment>> {
    const response = await request.service.get<Page<CustomerAdvancePayment>>("/customer/advance-payment/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 用户账户明细账单分页接口和 customerId payload。 */
export async function loadUserAccountBills(query: AccountBillQuery): Promise<Page<ServiceCostBillRow>> {
    const response = await request.service.get<Page<ServiceCostBillRow>>("/admin/service-cost-bill/paging/find-list", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 预付款撤销接口。 */
export async function revokeUserAdvancePayment(params: { id: number | string; remark: string }) {
    const response = await request.service.put("/customer/advance-payment/revoke", params);
    return response.data;
}

const display = (value?: string | number | null) =>
    value === undefined || value === null || value === "" ? DEFAULT_VALUE : value;

/** 费用管理-用户账户详情，组合真实的预付款和明细账单 React 深层组件。 */
export function UserAccountDetailPage({
    accountId,
    loadAccount = loadUserAccount,
    loadAdvancePayments = loadUserAdvancePayments,
    loadBills = loadUserAccountBills,
    revokeAdvancePayment = revokeUserAdvancePayment,
    verifyBill,
    revokeBill
}: UserAccountDetailPageProps) {
    const params = useParams<{ id?: string }>();
    const navigate = useNavigate();
    const currentId = accountId ?? params.id ?? "";
    const [account, setAccount] = useState<CustomerAccount>();
    const [activeTab, setActiveTab] = useState<"advance" | "bill">("advance");
    const [errorMessage, setErrorMessage] = useState("");

    const refreshAccount = useCallback(() => {
        if (!currentId) return;
        void loadAccount(currentId)
            .then(setAccount)
            .catch(() => setErrorMessage("用户账户详情加载失败"));
    }, [currentId, loadAccount]);

    useEffect(() => {
        refreshAccount();
    }, [refreshAccount]);

    return (
        <section
            aria-label="user-account-detail-page"
            className="detail-page-transparent flex min-h-full flex-col gap-4"
        >
            <div className="section-card">
                <ZhDetailHeader title="账户信息" />
                {errorMessage ? <Alert className="mb-4" message={errorMessage} role="alert" type="error" /> : null}
                <ZhGrid className="mt-5" cols={4} gap={[30, 20]}>
                    <ZhBaseItem label="用户账号" value={display(account?.name)} />
                    <ZhBaseItem label="用户ID" value={display(account?.code)} />
                    <ZhBaseItem
                        label="账户余额"
                        value={
                            account?.balance == null ? DEFAULT_VALUE : `${formatNumber(Number(account.balance), 2)}元`
                        }
                    />
                </ZhGrid>
                <div className="mt-6 flex items-end border-b border-blue-200" role="tablist" aria-label="账户记录类型">
                    <button
                        className={`h-10 w-28 rounded-t border px-3 text-base ${
                            activeTab === "advance"
                                ? "border-blue-200 border-b-2 border-b-blue-500 bg-blue-50 font-medium text-blue-600"
                                : "border-gray-300 border-b-0 bg-gray-50 text-slate-500"
                        }`}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "advance"}
                        onClick={() => setActiveTab("advance")}
                    >
                        预付款记录
                    </button>
                    <button
                        className={`h-10 w-28 rounded-t border px-3 text-base ${
                            activeTab === "bill"
                                ? "border-blue-200 border-b-2 border-b-blue-500 bg-blue-50 font-medium text-blue-600"
                                : "border-gray-300 border-b-0 bg-gray-50 text-slate-500"
                        }`}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === "bill"}
                        onClick={() => setActiveTab("bill")}
                    >
                        明细账单
                    </button>
                </div>
            </div>
            <div className="section-card">
                {activeTab === "advance" ? (
                    <WillPayRecord
                        id={currentId}
                        loadRecords={loadAdvancePayments}
                        revokeRecord={revokeAdvancePayment}
                        onUpdated={refreshAccount}
                    />
                ) : (
                    <BillRecord
                        id={currentId}
                        loadBills={loadBills}
                        verifyBill={verifyBill}
                        revokeBill={revokeBill}
                        onUpdated={refreshAccount}
                    />
                )}
            </div>
            <Space className="operation-fixed-btn" size={0}>
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </Space>
        </section>
    );
}

export default UserAccountDetailPage;
